<template>
  <div id="app">
    <div class="container">
      <div class="columns">
        <div class="column is-half">
          <text-field label="Source:" v-model="source.path" :valid=sourcePathIsValid invalidMessage="Not a valid path" />
          <checkbox v-if="!destination.ssh" v-model="source.ssh">SSH</checkbox>
          <div v-if="source.ssh">
          </div>
        </div>
        <div class="column is-half">
            <text-field label="Destination:" v-model="destination.path" :valid=destinationPathIsValid invalidMessage="Not a valid path" />
            <checkbox v-if="!source.ssh" v-model="destination.ssh">SSH</checkbox>
        </div>
      </div>

      <div v-if="source.ssh || destination.ssh">
        <secure-shell-form v-model="ssh" />
      </div>

      <div class="columns">
        <div class="column is-half">
          <rsync-script :cmd=cmd />
        </div>
        <div class="column is-half">
          <checkbox v-model="verbose">Verbose output</checkbox>
        </div>
      </div>

      <a @click="showAdvancedConfig = !showAdvancedConfig">{{ showAdvancedConfig ? 'Hide advanced options' : 'Advanced options'}}</a>
      <div v-if="showAdvancedConfig">
        <text-field label="Base options:" v-model="baseOptions" :valid="true" invalidMessage="Contains invalid options" />
        <checkbox v-model="deleteFilesOnDestination">Delete files on destination</checkbox>
        <select-single v-if="deleteFilesOnDestination" v-model="deleteType" :options="deleteTypeOptions" />
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from './components/Checkbox.vue'
import RsyncScript from './components/RsyncScript.vue'
import SecureShellForm from './components/SecureShellForm.vue'
import SelectSingle from './components/SelectSingle.vue'
import TextField from './components/TextField.vue'

export default {
  name: 'app',
  data() {
    return {
      baseOptions: '-a',
      source: {
        path: '/path',
        ssh: false
      },
      destination: {
        path: '/path',
        ssh: false
      },
      ssh: {
        host: '',
        user: '',
        port: '',
        keyFile: ''
      },
      deleteFilesOnDestination: false,
      deleteType: '--delete',
      deleteTypeOptions: ['--delete', '--delete-before', '--delete-during', '--delete-after', '--delete-delay', '--delete-excluded'],
      showAdvancedConfig: false,
      verbose: false
    }
  },
  components: {
    Checkbox,
    RsyncScript,
    SecureShellForm,
    SelectSingle,
    TextField
  }, 
  computed: {
    cmd() {

      let cmdOut = '#!/bin/bash\n\n'

      // Base 
      const rsyncCmd = ['rsync']

      // Verbosity
      if (this.baseOptions !== '' && this.verbose) {
        rsyncCmd.push(`${this.baseOptions}v`)
      } else if (this.baseOptions !== '' && !this.verbose) {
        rsyncCmd.push(this.baseOptions)
      } else if (this.verbose) {
        rsyncCmd.push('--verbose')
      }

      // Delete and backup
      if (this.deleteFilesOnDestination) rsyncCmd.push(this.deleteType)

      // SSH
      if (this.sshConfig) rsyncCmd.push(this.sshConfig)

      // Paths
      rsyncCmd.push(this.sourcePath, this.destinationPath)

      // Add comments
      rsyncCmd.push(`\n\n${this.comments}`)
      cmdOut += rsyncCmd.join(' ')

      return cmdOut
    },
    sourcePath() {
      if (this.source.path && this.source.ssh) {
        return `${this.sshUserAndHost}${this.source.path}`
      } else if (this.source.path) {
        return this.source.path
      } else {
        return '<SOURCE PATH>'
      }
    },
    destinationPath() {
      if (this.destination.path && this.destination.ssh) {
        return `${this.sshUserAndHost}${this.destination.path}`
      } else if (this.destination.path) {
        return this.destination.path
      } else {
        return '<DESTINATION PATH>'
      }
    },
    sshUserAndHost() {
      if (this.ssh.host && this.ssh.user) {
        return `${this.ssh.user}@${this.ssh.host}:`
      } else {
        return ''
      } 
    },
    sshConfig() {
      const sshConfList = []

      if (this.ssh.port) {
        sshConfList.push(`-p ${this.ssh.port}`)
      }

      if (this.ssh.keyFile) {
        sshConfList.push(`-i ${this.ssh.keyFile}`)
      }

      if (sshConfList.length !== 0) {
        const sshConfString = sshConfList.join(' ')
        return `-e "${sshConfString}"` 
      }

      return false
    },
    comments() {
      const commentList = []
      commentList.push('# Options:')
      commentList.push('# -a, --archive      archive mode; equals -rlptgoD (no -H,-A,-X)')
      
      if (this.deleteFilesOnDestination) {
        commentList.push('# --delete           delete extraneous files from dest dirs')
      }

      if (this.ssh.user && this.ssh.host) {
        commentList.push(`# ${this.ssh.user}@${this.ssh.host}:   remote user and host`)
      }


      if (this.ssh.port || this.ssh.keyFile) {
        commentList.push('# -e, --rsh=COMMAND           specify the remote shell to use')
      }

      if (this.verbose) {
        commentList.push('# -v, --verbose               increase verbosity')
      }

      return commentList.join('\n')
    },
    sourcePathIsValid() {
      return validatePath(this.source.path)
    },
    destinationPathIsValid() {
      return validatePath(this.destination.path)
    }
  }
}

// Helpers

function validatePath(path) {
  const pathRegex = RegExp('^(/[^/ ]*)+/?$')
  return pathRegex.test(path)
}
</script>

<style lang="scss">
@import 'scss/main';
</style>
